/* Responsibility: Own app-facing transactional email capabilities behind one transport-agnostic service boundary.
Scope: Validates input, gathers feature-specific context, resolves auth, and delegates email-triggering requests without leaking backend mechanics to callers. */
import * as gameStore from '../game-store.js'
import { authStore } from '../ui/stores/auth-store.js'
import { getAppImportMetaEnv } from "@anyquest/shared-core"

interface FeedbackDiagnosticData {
  url: string
  ua: string
  saveId?: string
  gameState: {
    roomName?: string
    historyLen?: number
    vitals: unknown
    activeActionId?: string
  } | null
}

type EmailActionReason =
  | 'validation'
  | 'submitted'
  | 'auth_required'
  | 'remote_failed'

export interface EmailActionResult {
  ok: boolean
  error: boolean
  message: string
  reason: EmailActionReason
}

const FEEDBACK_FUNCTION_NAME = 'submitFeedback'
const INVITE_FUNCTION_NAME = 'inviteUser'
const appEnv = getAppImportMetaEnv()

export const emailService = {
  async submitFeedback(rawText: string): Promise<EmailActionResult> {
    const text = String(rawText ?? '').trim()
    if (!text || text.length < 3) {
      return failure('Too short.', 'validation')
    }

    const auth = await readAuthToken('Sign-in required for feedback submission.')
    if (!auth.ok) return auth

    try {
      const message = await postAuthorizedJson(
        resolveFunctionEndpoint('VITE_FEEDBACK_SUBMIT_URL', FEEDBACK_FUNCTION_NAME),
        {
          text,
          diagnosticData: readFeedbackContext(),
        },
        getAuthToken(auth),
      )
      return success(message)
    } catch (error) {
      console.error('[EmailService] Feedback submit failed:', error)
      return failure(readErrorMessage(error, 'Remote submit failed.'), 'remote_failed')
    }
  },

  async sendAdminInvite(rawEmail: string): Promise<EmailActionResult> {
    const email = String(rawEmail ?? '').trim().toLowerCase()
    if (!email || !email.includes('@')) {
      return failure('A valid email is required.', 'validation')
    }

    const auth = await readAuthToken('Sign-in required to send invite.')
    if (!auth.ok) return auth

    try {
      const message = await postAuthorizedJson(
        resolveFunctionEndpoint('VITE_INVITE_USER_URL', INVITE_FUNCTION_NAME),
        { email },
        getAuthToken(auth),
      )
      return success(message || `Invite emailed to ${email}`)
    } catch (error) {
      console.error('[EmailService] Invite send failed:', error)
      return failure(readErrorMessage(error, 'Invitation failed.'), 'remote_failed')
    }
  },
}

function success(message: string): EmailActionResult {
  return {
    ok: true,
    error: false,
    message,
    reason: 'submitted',
  }
}

function failure(
  message: string,
  reason: Exclude<EmailActionReason, 'submitted'>,
): EmailActionResult {
  return {
    ok: false,
    error: true,
    message,
    reason,
  }
}

async function readAuthToken(
  missingMessage: string,
): Promise<{ ok: true, value: string } | EmailActionResult> {
  try {
    const idToken = await authStore.user?.getIdToken()
    if (idToken) return { ok: true, value: idToken }
  } catch (error) {
    console.error('[EmailService] Failed to read auth token:', error)
  }

  return failure(missingMessage, 'auth_required')
}

function getAuthToken(auth: { ok: true; value: string } | EmailActionResult): string {
  if ('value' in auth) return auth.value
  throw new Error(auth.message)
}

function readFeedbackContext(): FeedbackDiagnosticData {
  const state = gameStore.state
  return {
    url: window.location.href,
    ua: navigator.userAgent,
    saveId: gameStore.activeSaveId ?? undefined,
    gameState: state ? {
      roomName: state.currentRoom?.name,
      historyLen: state.history?.length,
      vitals: state.player?.vitals,
      activeActionId: (state as { activeActionId?: string }).activeActionId,
    } : null,
  }
}

async function postAuthorizedJson(url: string, body: unknown, idToken: string): Promise<string> {
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
    },
    body: JSON.stringify(body),
  })

  const result = await response.json().catch(() => ({}))
  if (!response.ok) {
    throw new Error(
      typeof result.error === 'string' && result.error.trim()
        ? result.error.trim()
        : typeof result.message === 'string' && result.message.trim()
          ? result.message.trim()
          : `Request failed. Status: ${response.status}`,
    )
  }

  return typeof result.message === 'string' && result.message.trim()
    ? result.message.trim()
    : 'Thank you!'
}

function resolveFunctionEndpoint(envKey: 'VITE_FEEDBACK_SUBMIT_URL' | 'VITE_INVITE_USER_URL', functionName: string): string {
  const configured = String(appEnv[envKey] ?? '').trim()
  if (configured) return configured

  const baseUrl = String(appEnv.VITE_EMAIL_GATEWAY_BASE_URL ?? '').trim().replace(/\/+$/, '')
  if (!baseUrl) return functionName

  return `${baseUrl}/${functionName}`
}

function readErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback
}
